/* The D-14 execution assessment. One POST, and it writes nothing to a customer's source.
 *
 * **A mutation hook for a read-shaped question, and that is correct rather than a compromise.** The
 * route is a POST because it dials a connection to a customer's production database and counts rows in
 * it — a rung-2 read somebody has to have decided to make — so it takes an `Idempotency-Key` and must
 * not fire on render. `useMutation` is what makes that impossible: there is no `enabled` flag, no
 * refetch-on-focus, and no way for a screen refresh to cause forty scans of a bank's tables.
 *
 * **The assessment is never cached across plans.** A stale assessment is worse than none: it was true
 * against an approval that may since have been spent and a residency policy that may since have
 * narrowed, and every one of those gates is re-run server-side on each call. So the result lives in the
 * mutation's own state and dies with it.
 */

import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import { isExecutionAssessment } from '@/shared/api/generated/remediation';
import type { ExecutionAssessment } from '@/shared/api/generated/remediation';
import { forgetIdempotencyKey, idempotencyKeyFor, post } from '@/shared/api/http';

export function useAssessExecution(): UseMutationResult<
  ExecutionAssessment,
  Error,
  string
> {
  return useMutation({
    mutationFn: async (planId: string) =>
      post(
        `/v1/remediation/plans/${planId}/execution-assessment`,
        /* Keyed on the plan and forgotten on success, so a retried request is the same assessment
         * and a deliberate re-assessment is a new one. The distinction matters more here than on most
         * routes: an assessment reads a customer's table, so a key that changed between retries would
         * turn one timed-out request into two scans. */
        idempotencyKeyFor('remediation-assess', planId),
        isExecutionAssessment,
        'execution assessment',
        {},
      ),
    onSuccess: (_result, planId) => {
      forgetIdempotencyKey('remediation-assess', planId);
    },
  });
}
